import {
  filterDefinitions,
  getFilterDefinition
} from '../catalog/filter_definitions'
import { filterItems } from '../catalog/filter_engine'
import sortDefinitions from '../catalog/sort_definitions'

/**
 * @class
 */
class CatalogModel {
  #items = []

  get items() {
    return structuredClone(this.#items)
  }

  setItems(items) {
    this.#items = structuredClone(items)
  }

  /**
   * Читает data-атрибуты DOM-узлов элементов каталога и формирует внутреннее представление.
   * Автоматически определяет список фильтров по `data-filter-*` атрибутам узлов.
   * @param {Element[]} nodes - DOM-узлы элементов каталога.
   */
  parseNodesIntoItems(nodes) {
    const nodeList = Array.from(nodes)
    const filterNames = this.#filterNames(nodeList)
    const items = nodeList.map((node, originalIndex) => {
      const filterData = [...filterNames].reduce((data, filterName) => {
        const definition = getFilterDefinition(filterName)
        const rawValue = node.getAttribute(definition.itemAttribute)
        data[filterName] = definition.parseItemValue(rawValue)

        return data
      }, {})

      return {
        id: String(node.dataset.id),
        originalIndex,
        createdAt: this.#numberValue(node.dataset.createdAt),
        timeZone: node.dataset.timeZone || null,
        ...filterData
      }
    })

    this.setItems(items)
  }

  /**
   * Возвращает массив id элементов, прошедших фильтрацию.
   * @param {Object.<string, *>} filters
   * @param {object} [context={}]
   * @returns {string[]}
   */
  filter(filters, context = {}) {
    const filteredItems = filterItems(this.#items, filters, context)
    return filteredItems.map((item) => item.id)
  }

  /**
   * Возвращает количество элементов, прошедших фильтрацию.
   * @param {Object.<string, *>} filters
   * @param {object} [context={}]
   * @returns {number}
   */
  count(filters, context = {}) {
    return this.filter(filters, context).length
  }

  /**
   * Вычисляет полный снимок: отфильтрованные элементы, порядок сортировки, страницу и список id для отображения.
   * @param {{ filters: object, sort: string, page: number, pageSize: number }} state
   * @param {object} [context={}]
   * @returns {{ orderedIds: string[], pageIds: string[], page: number, pageSize: number, totalItems: number, totalPages: number }}
   */
  getSnapshot(state, context = {}) {
    const filteredItems = filterItems(this.#items, state.filters, context)
    const compareItems = sortDefinitions[state.sort] || sortDefinitions.default
    const orderedItems = [...filteredItems].sort(compareItems)
    const totalItems = orderedItems.length
    const pageSize = this.#positiveInteger(state.pageSize, totalItems || 1)
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
    const requestedPage = this.#positiveInteger(state.page, 1)
    const page = Math.min(requestedPage, totalPages)
    const pageStart = (page - 1) * pageSize
    const pageItems = orderedItems.slice(pageStart, pageStart + pageSize)

    return {
      orderedIds: orderedItems.map((item) => item.id),
      pageIds: pageItems.map((item) => item.id),
      page,
      pageSize,
      totalItems,
      totalPages
    }
  }

  #numberValue(rawValue) {
    if (
      rawValue === null ||
      typeof rawValue === 'undefined' ||
      String(rawValue).trim() === ''
    ) {
      return null
    }

    const value = Number(rawValue)

    return Number.isFinite(value) ? value : null
  }

  #filterNames(nodes) {
    const filterNames = new Set(
      Object.keys(filterDefinitions).filter((filterName) => {
        return filterName !== 'default'
      })
    )

    nodes.forEach((node) => {
      Array.from(node.attributes).forEach((attribute) => {
        if (!attribute.name.startsWith('data-filter-')) return

        filterNames.add(attribute.name.replace(/^data-filter-/, ''))
      })
    })

    return filterNames
  }

  #positiveInteger(value, fallback) {
    return Number.isInteger(value) && value > 0 ? value : fallback
  }
}

export { CatalogModel }
export default new CatalogModel()
